// Recovery from the "Loading chunk NNNN failed" screen.
//
// After a deploy, a tab still running the OLD build asks for lazily-loaded
// chunk files named by the old content hash. If those files are gone from the
// server the request 404s and the route never renders.
//
// Two places have to handle it, because they see different events:
//   - index.js listens on window 'error' / 'unhandledrejection', which catches
//     a chunk failure outside React's tree;
//   - AppErrorBoundary handles the one React catches. React does NOT re-throw
//     to window.onerror once an error boundary handles it, so without the
//     boundary calling in here the auto-reload never ran and the person was
//     left reading the raw error and clicking Reload themselves.

export const isChunkError = (v) => {
  const s = String((v && (v.message || (v.reason && (v.reason.message || v.reason)))) || v || '');
  return /Loading chunk|ChunkLoadError|Loading CSS chunk/i.test(s);
};

/** True once a reload has already been attempted this session. */
export const alreadyTriedReload = () => {
  try {
    return sessionStorage.getItem('chunk-reloaded') === '1';
  } catch (e) {
    return false;
  }
};

export const reloadOnceForChunk = (evt) => {
  try {
    if (sessionStorage.getItem('chunk-reloaded') === '1') return false;
    sessionStorage.setItem('chunk-reloaded', '1');
  } catch (e) {
    /* private mode — fall through and reload anyway */
  }
  // A hard reload does NOT bypass the HTTP cache for lazily-loaded chunks, so a
  // poisoned cache entry (HTML cached under a chunk URL) survives reloads.
  // Heal it first: refetch the failing chunk with cache:'reload', which replaces
  // the cached entry with a fresh copy from the network — THEN reload.
  const msg = String((evt && (evt.message || (evt.reason && evt.reason.message))) || evt || '');
  const m = msg.match(/https?:\/\/[^\s)]+\.chunk\.(?:js|css)/);
  const heal = m ? fetch(m[0], { cache: 'reload' }).catch(() => {}) : Promise.resolve();
  heal.finally(() => window.location.reload());
  return true;
};

/** Clear the guard once a build has loaded cleanly, so a future deploy can heal too. */
export const clearReloadGuardWhenHealthy = (afterMs = 4000) => {
  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem('chunk-reloaded');
      } catch (e) {
        /* ignore */
      }
    }, afterMs);
  });
};
